<script setup lang="ts">
import { computed, onBeforeMount, provide, ref, watch } from 'vue';
import { Router } from 'vue-router';

import {
  FilterMatchMode,
  TSButton,
  TSButtonFilter,
  TSButtonSearch,
  TSDialogConfirm,
  useLoadingStore,
  useToast,
} from 'tsv2-library';

import BorrowingServices from '@/services/borrowing.service';

import useTableStore from '@/store/table.store';

import LinkedAssetDialog from '@/components/common/Dialog/LinkedAssetDialog.vue';
import BorrowExtensionBulkAction from './BorrowExtensionBulkAction.vue';
import BorrowExtensionFilter from './BorrowExtensionFilter.vue';
import BorrowExtensionTable from './BorrowExtensionTable.vue';

import {
  PutBorrowingEditExtensionDTO,
  PutBorrowingExtendRequestDTO,
} from '@/dto/borrowingExtend.dto';

import { getSettingTransactionData } from '@/utils/getData.utils';

import { BorrowingTransactionRequest } from '@/types/borrowing.type';
import { BorrowingRequestFilters } from '@/types/filter.type';
import { BorrowingRequestFetchParams } from '@/types/params.type';
import { SettingTransaction } from '@/types/setting-transaction.type';

onBeforeMount(async () => {
  try {
    await getSettingTransactionData(setLoading, settingTransaction, toast);

    if (
      sessionStorage.getItem('assetBorrowRequestExtension') ||
      sessionStorage.getItem('assetBorrowRequestExtensionTransactionId')
    ) {
      // Prioritize getting the request ids from transaction id.
      if (sessionStorage.getItem('assetBorrowRequestExtensionTransactionId')) {
        const { data } = await BorrowingServices.getBorrowingTransactionRequest(
          sessionStorage.getItem('assetBorrowRequestExtensionTransactionId') ??
            '',
          {
            status: JSON.stringify(['On Borrowing']),
          },
        );

        borrowRequestIds.value = data.data.data.map((datum) => datum._id);
      } else {
        borrowRequestIds.value = JSON.parse(
          sessionStorage.getItem('assetBorrowRequestExtension') as string,
        );
      }

      await getTransactionRequestData();

      mode.value =
        sessionStorage.getItem('assetBorrowRequestExtensionMode') ?? undefined;

      window.dispatchEvent(
        new CustomEvent('updateTable', {
          detail: {
            tableName: 'borrow-extension-table',
          },
        }),
      );
    } else {
      throw new Error();
    }
  } catch {
    handleCancel();
  }
});

const props = defineProps<{
  router?: Router;
}>();

const toast = useToast();
const { incrementTableKey } = useTableStore();
const { setLoading } = useLoadingStore();

const selectedData = ref<BorrowingTransactionRequest[]>([]);

const borrowRequestIds = ref<string[]>([]);

const mode = ref<string>();

const formData = ref<Map<string, BorrowingTransactionRequest>>(new Map());

const isValid = ref<boolean>(false);

const showFilter = ref<boolean>(false);
const showCancelConfirm = ref<boolean>(false);
const showLinkedAssetConfirm = ref<boolean>(false);

const defaultQueryParams = computed<BorrowingRequestFetchParams>(() => {
  return {
    requestIds: JSON.stringify(borrowRequestIds.value),
  };
});

const settingTransaction = ref<SettingTransaction>();

const filterParams = ref<{
  [key: string]: {
    value?: null | string[] | number[];
    matchMode: number;
  };
}>({
  'name.name': { value: null, matchMode: FilterMatchMode.IN },
  'brand.name': { value: null, matchMode: FilterMatchMode.IN },
  'model.name': { value: null, matchMode: FilterMatchMode.IN },
  'dueDateInt': { value: null, matchMode: FilterMatchMode.BETWEEN },
});

const searchModel = ref<string>();

const formDataArray = computed<BorrowingTransactionRequest[]>(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return Array.from(formData.value, ([_, value]) => value);
});

/**
 * Get all transaction request according to requestIds data.
 */
const getTransactionRequestData = async (): Promise<void> => {
  try {
    setLoading(true);

    const { data } = await BorrowingServices.getBorrowingTransactionRequestList(
      defaultQueryParams.value,
    );

    data.data.data.forEach((datum) => {
      formData.value.set(datum._id, {
        ...datum,
        dueDateInt: Date.parse(datum.dueDate.toString()),
      });
    });
  } catch (error) {
    console.error(error);
    toast.add({
      error,
      message: 'Error, failed to fetch borrow transaction request.',
      severity: 'error',
    });
  } finally {
    setLoading(false);
  }
};

/**
 * Handle the cancelation to extend borrow asset.
 */
const handleCancel = (): void => {
  sessionStorage.removeItem('assetBorrowRequestExtension');
  sessionStorage.removeItem('assetBorrowRequestExtensionMode');
  sessionStorage.removeItem('assetBorrowBreadcrumb');
  sessionStorage.removeItem('assetBorrowRequestExtensionTransactionId');

  if (props.router) props.router.back();
};

/**
 * Handle the submission of the request extension to borrow asset.
 */
const handleSubmit = async (): Promise<void> => {
  if (!isValid.value) return;
  try {
    setLoading(true);

    if (mode.value === 'Edit') {
      const dataDTO: PutBorrowingEditExtensionDTO[] = formDataArray.value.map(
        (datum) => ({
          duration: datum.duration,
          id: datum._id,
        }),
      );

      await BorrowingServices.putBorrowingEditExtension(dataDTO);
    } else {
      const dataDTO: PutBorrowingExtendRequestDTO[] = formDataArray.value.map(
        (datum) => ({
          duration: datum.duration,
          request: datum._id,
        }),
      );

      await BorrowingServices.putBorrowingExtendRequest(dataDTO);
    }

    toast.add({
      message: `Success, extension request has been ${mode.value === 'Edit' ? 'edited' : 'submited'}.`,
      severity: 'success',
    });

    incrementTableKey();

    sessionStorage.removeItem('assetBorrowRequestExtension');
    sessionStorage.removeItem('assetBorrowRequestExtensionMode');
    sessionStorage.removeItem('assetBorrowBreadcrumb');
    sessionStorage.removeItem('assetBorrowRequestExtensionTransactionId');

    if (props.router) props.router.back();
  } catch (error) {
    toast.add({
      error,
      message: 'Error, failed to submit extension request.',
      severity: 'error',
    });
  } finally {
    setLoading(false);
  }
};

watch(
  formData,
  () => {
    const isDurationFilledAll = formDataArray.value.every(
      (datum) => datum.duration,
    );

    if (formDataArray.value.length && isDurationFilledAll) {
      isValid.value = true;
    } else {
      isValid.value = false;
    }
  },
  { deep: true },
);

const assignFilterParams = (filters: BorrowingRequestFilters): void => {
  filterParams.value['name.name'].value = filters.name;
  filterParams.value['brand.name'].value = filters.brand;
  filterParams.value['model.name'].value = filters.model;
  filterParams.value['dueDateInt'].value = filters.dueDate;
};

const isLinkedAsset = computed(() =>
  formDataArray.value.some(
    (datum) => datum.children && datum.children.length > 0,
  ),
);

provide('formData', formData);
provide('settingTransaction', settingTransaction);
provide('isLinkedAsset', isLinkedAsset);
</script>
<template>
  <LinkedAssetDialog
    v-model:visible="showLinkedAssetConfirm"
    :data="formDataArray"
    @confirm="handleSubmit"
    mode="Confirmation"
  />
  <div
    v-if="settingTransaction"
    class="flex flex-col gap-3"
    data-ts-section="borrow-extension-header"
  >
    <div
      class="flex flex-row justify-between items-center"
      data-ts-section="borrow-extension-header-title"
    >
      <span
        class="text-[16px] text-general-800 font-bold leading-[normal] tracking-[0.32px]"
        data-ts-section="borrow-extension-title"
      >
        Extension Request
      </span>
      <span
        class="text-xs font-medium text-general-800 leading-[normal] tracking-[0.24px]"
        data-ts-section="borrow-extension-description"
      >
        Max. Borrowing Time:
        {{ settingTransaction.borrowingAttrs.maxBorrowingTime }} day(s)
      </span>
    </div>
    <div
      class="flex flex-row justify-between items-center"
      data-ts-section="borrow-extension-header-group"
    >
      <BorrowExtensionBulkAction
        v-model:form-data="formData"
        v-model:selected-data="selectedData"
      />
      <div
        class="flex flex-row gap-2 items-center"
        data-ts-section="borrow-extension-header-action-group"
      >
        <div
          class="flex flex-row gap-1 items-center"
          data-ts-section="borrow-extension-header-action"
        >
          <TSButtonSearch
            @search="searchModel = $event"
            data-ts-section="borrow-extension-search-button"
          />
          <TSButtonFilter
            v-model:show-filter="showFilter"
            data-ts-section="borrow-extension-filter-button"
          />
        </div>
      </div>
    </div>
  </div>
  <BorrowExtensionFilter
    v-show="showFilter"
    :assets="formDataArray"
    @apply-filter="assignFilterParams"
  />
  <BorrowExtensionTable
    v-model:form-data="formData"
    v-model:selected-data="selectedData"
    :data="formDataArray"
    :filters="filterParams"
    :search="searchModel"
    :use-paginator="!!formDataArray.length"
  />
  <TSDialogConfirm
    v-model:visible="showCancelConfirm"
    @confirm="handleCancel"
    class="text-general-800 text-xs font-medium leading-[normal] tracking-[0.24px]"
    confirm-label="Continue"
    header="Cancel Extension Request"
    severity="danger"
  >
    <template #body>
      <span>This action will cancel the request.</span>
      <span>Are you sure want to continue?</span>
    </template>
  </TSDialogConfirm>
  <div
    class="flex flex-col gap-1 items-end"
    data-ts-section="borrow-extension-footer"
  >
    <div class="flex gap-1" data-ts-section="borrow-extension-button-footer">
      <TSButton
        @click="showCancelConfirm = true"
        data-ts-section="cancel-button"
        label="Cancel"
        severity="dark"
        text
      />
      <TSButton
        @click="
          isLinkedAsset ? (showLinkedAssetConfirm = true) : handleSubmit()
        "
        data-ts-section="submit-button"
        label="Submit"
        severity="success"
      />
    </div>
  </div>
</template>
